import { useState, useRef } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Upload,
  Trash2,
  Eye,
  EyeOff,
  GripVertical,
  Image as ImageIcon,
  Video,
  Plus,
  Clock,
  Loader2,
  Play,
  MonitorPlay,
  Link2,
  Youtube,
  Instagram,
  Pencil,
  Check,
  X,
  RefreshCw,
  CalendarClock,
  ListVideo,
} from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import {
  getMediaTitle,
  MEDIA_DISPLAY_DURATION_MAX_SECONDS,
  MEDIA_DISPLAY_DURATION_MIN_SECONDS,
  normalizeMediaDisplayDuration,
  getPromotionalMediaSizeError,
} from "@shared/mediaFormValidation";

type MediaItem = {
  id: number;
  type: "image" | "video";
  url: string;
  fileKey: string;
  title: string | null;
  displayDuration: number | null;
  sortOrder: number | null;
  isActive: boolean;
  startHour: number | null;
  endHour: number | null;
  externalUrl: string | null;
  externalType: "youtube" | "instagram" | "admooh" | null;
  embedHtml?: string | null;
  playlistId?: number | null;
  createdAt: Date;
};

// ─── Helpers para extrair embed URLs ─────────────────────────────────────────
function getYoutubeEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    let videoId: string | null = null;
    if (u.hostname.includes("youtu.be")) {
      videoId = u.pathname.slice(1);
    } else if (u.hostname.includes("youtube.com")) {
      videoId = u.searchParams.get("v") ?? u.pathname.split("/").pop() ?? null;
    }
    if (!videoId) return null;
    return `https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&loop=1&playlist=${videoId}&controls=0&modestbranding=1&rel=0`;
  } catch {
    return null;
  }
}

function getInstagramEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url);
    if (!u.hostname.includes("instagram.com")) return null;
    // Normalizar: remover trailing slash e parâmetros
    const path = u.pathname.replace(/\/$/, "");
    return `https://www.instagram.com${path}/embed/`;
  } catch {
    return null;
  }
}

function normalizeInstagramUrl(rawUrl: string): string | null {
  try {
    const u = new URL(rawUrl);
    const host = u.hostname.toLowerCase().replace(/^www\./, "").replace(/^m\./, "");
    if (host !== "instagram.com") return null;
    const parts = u.pathname.split("/").filter(Boolean);
    const contentType = parts[0] === "reels" ? "reel" : parts[0];
    if (!parts[1] || !["reel", "p", "tv"].includes(contentType)) return null;
    return `https://www.instagram.com/${contentType}/${parts[1]}/`;
  } catch {
    return null;
  }
}

function detectExternalType(url: string): "youtube" | "instagram" | "admooh" | null {
  try {
    const u = new URL(url);
    if (u.hostname.includes("youtube.com") || u.hostname.includes("youtu.be")) return "youtube";
    if (u.hostname.includes("instagram.com")) return "instagram";
    if (u.hostname.includes("admooh.com")) return "admooh";
    return null;
  } catch {
    return null;
  }
}

// ─── Modal de horário de exibição ─────────────────────────────────────────────
function ScheduleModal({
  item,
  onClose,
  onSave,
}: {
  item: MediaItem;
  onClose: () => void;
  onSave: (startHour: number | null, endHour: number | null) => void;
}) {
  const [startHour, setStartHour] = useState<string>(
    item.startHour != null ? String(item.startHour).padStart(2, "0") + ":00" : ""
  );
  const [endHour, setEndHour] = useState<string>(
    item.endHour != null ? String(item.endHour).padStart(2, "0") + ":00" : ""
  );

  function parseHour(val: string): number | null {
    if (!val) return null;
    return parseInt(val.split(":")[0], 10);
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="w-4 h-4" />
            Horário de Exibição
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Defina o período em que esta mídia deve aparecer no Painel de TV.
            Deixe em branco para exibir o dia todo.
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="start-hour">Início</Label>
              <Input id="start-hour" type="time" step="3600" value={startHour}
                onChange={(e) => setStartHour(e.target.value)} className="mt-1.5" placeholder="09:00" />
            </div>
            <div>
              <Label htmlFor="end-hour">Fim</Label>
              <Input id="end-hour" type="time" step="3600" value={endHour}
                onChange={(e) => setEndHour(e.target.value)} className="mt-1.5" placeholder="18:00" />
            </div>
          </div>
          {startHour && endHour && (
            <p className="text-xs text-muted-foreground bg-muted rounded-lg px-3 py-2">
              Esta mídia será exibida das <strong>{startHour}</strong> às <strong>{endHour}</strong>.
            </p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { setStartHour(""); setEndHour(""); onSave(null, null); onClose(); }}>
            Sem restrição
          </Button>
          <Button onClick={() => { onSave(parseHour(startHour), parseHour(endHour)); onClose(); }}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Edição inline de duração ─────────────────────────────────────────────────
function DurationEditor({ item, onSave }: { item: MediaItem; onSave: (dur: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(item.displayDuration ?? 8));

  if (!editing) {
    return (
      <button
        className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors group"
        onClick={() => setEditing(true)}
        title="Clique para editar o tempo de exibição"
      >
        <Clock className="w-3 h-3" />
        <span>{item.displayDuration ?? 8}s</span>
        <Pencil className="w-2.5 h-2.5 opacity-0 group-hover:opacity-60 transition-opacity" />
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <input
        type="number"
        min={MEDIA_DISPLAY_DURATION_MIN_SECONDS}
        max={MEDIA_DISPLAY_DURATION_MAX_SECONDS}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        className="w-14 h-6 text-xs border rounded px-1.5 bg-background"
        autoFocus
        onKeyDown={(e) => {
          if (e.key === "Enter") { onSave(normalizeMediaDisplayDuration(Number(val))); setEditing(false); }
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <span className="text-xs text-muted-foreground">s</span>
      <button onClick={() => { onSave(normalizeMediaDisplayDuration(Number(val))); setEditing(false); }}
        className="text-green-500 hover:text-green-400"><Check className="w-3 h-3" /></button>
      <button onClick={() => setEditing(false)}
        className="text-muted-foreground hover:text-foreground"><X className="w-3 h-3" /></button>
    </div>
  );
}

// ─── Thumbnail para links externos ────────────────────────────────────────────
function ExternalThumbnail({ item }: { item: MediaItem }) {
  if (item.externalType === "youtube") {
    const embedUrl = getYoutubeEmbedUrl(item.externalUrl ?? "");
    // Extrair videoId para thumbnail
    let thumbUrl = "";
    try {
      const u = new URL(item.externalUrl ?? "");
      let vid = u.searchParams.get("v") ?? u.pathname.split("/").pop() ?? "";
      if (u.hostname.includes("youtu.be")) vid = u.pathname.slice(1);
      thumbUrl = `https://img.youtube.com/vi/${vid}/mqdefault.jpg`;
    } catch {}
    return (
      <div className="w-full h-full relative bg-black flex items-center justify-center">
        {thumbUrl && <img src={thumbUrl} alt="YouTube" className="w-full h-full object-cover opacity-80" />}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-10 h-10 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
            <Youtube className="w-5 h-5 text-white" />
          </div>
        </div>
      </div>
    );
  }
  if (item.externalType === "instagram") {
    return (
      <div className="w-full h-full bg-gradient-to-br from-purple-600 via-pink-500 to-orange-400 flex items-center justify-center flex-col gap-2">
        <Instagram className="w-10 h-10 text-white" />
        {item.embedHtml ? (
          <span className="text-xs text-white bg-green-500/80 px-2 py-0.5 rounded-full">✓ Embed OK</span>
        ) : (
          <span className="text-xs text-white bg-red-500/80 px-2 py-0.5 rounded-full">Sem embed</span>
        )}
      </div>
    );
  }
  if (item.externalType === "admooh") {
    return (
      <div className="w-full h-full bg-gradient-to-br from-sky-700 via-blue-600 to-indigo-800 flex items-center justify-center flex-col gap-2 text-white">
        <MonitorPlay className="w-10 h-10" />
        <span className="text-xs rounded-full bg-white/20 px-2 py-0.5">Fonte Admoon</span>
      </div>
    );
  }
  return null;
}

// ─── Componente principal ─────────────────────────────────────────────────────
export default function AdminMedia() {
  const utils = trpc.useUtils();
  const [uploadOpen, setUploadOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [previewItem, setPreviewItem] = useState<MediaItem | null>(null);
  const [scheduleItem, setScheduleItem] = useState<MediaItem | null>(null);
  const [dragOverId, setDragOverId] = useState<number | null>(null);
  const dragItemRef = useRef<number | null>(null);

  // Upload form state
  const [file, setFile] = useState<File | null>(null);
  const [filePreview, setFilePreview] = useState<string | null>(null);
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState(8);
  const [uploading, setUploading] = useState(false);

  // Link externo form state
  const [externalUrl, setExternalUrl] = useState("");
  const [externalTitle, setExternalTitle] = useState("");
  const [externalDuration, setExternalDuration] = useState(30);
  const [addingLink, setAddingLink] = useState(false);
  const [playlistId, setPlaylistId] = useState<string>("none");
  const [playlistManagerOpen, setPlaylistManagerOpen] = useState(false);
  const [playlistName, setPlaylistName] = useState("");
  const [playlistStartDate, setPlaylistStartDate] = useState("");
  const [playlistEndDate, setPlaylistEndDate] = useState("");
  const [playlistStartHour, setPlaylistStartHour] = useState("");
  const [playlistEndHour, setPlaylistEndHour] = useState("");
  const [playlistDays, setPlaylistDays] = useState<number[]>([0, 1, 2, 3, 4, 5, 6]);

  const detectedType = detectExternalType(externalUrl);
  const safeDuration = normalizeMediaDisplayDuration(duration);
  const safeExternalDuration = normalizeMediaDisplayDuration(externalDuration, 30);

  const { data: mediaList = [], isLoading } = trpc.media.list.useQuery();
  const { data: playlists = [] } = (trpc as any).media.listPlaylists.useQuery();

  const uploadMutation = trpc.media.upload.useMutation({
    onSuccess: () => { (utils as any).media.list.invalidate(); setUploadOpen(false); resetForm(); toast.success("Mídia enviada com sucesso!"); },
    onError: (e: any) => toast.error("Erro ao enviar: " + e.message),
  });

  const addLinkMutation = (trpc as any).media.addExternalLink.useMutation({
    onSuccess: () => {
      (utils as any).media.list.invalidate();
      setUploadOpen(false);
      setExternalUrl(""); setExternalTitle(""); setExternalDuration(30);
      toast.success("Link adicionado com sucesso!");
    },
    onError: (e: any) => toast.error("Erro ao adicionar link: " + e.message),
  });

  const createPlaylistMutation = (trpc as any).media.createPlaylist.useMutation({
    onSuccess: () => {
      (utils as any).media.listPlaylists.invalidate();
      setPlaylistName(""); setPlaylistStartDate(""); setPlaylistEndDate(""); setPlaylistStartHour(""); setPlaylistEndHour(""); setPlaylistDays([0, 1, 2, 3, 4, 5, 6]);
      toast.success("Playlist programada criada!");
    },
    onError: (e: any) => toast.error("Erro ao criar playlist: " + e.message),
  });

  const updateMutation = trpc.media.update.useMutation({
    onSuccess: () => (utils as any).media.list.invalidate(),
    onError: (e: any) => toast.error("Erro ao atualizar: " + e.message),
  });

  const deleteMutation = trpc.media.delete.useMutation({
    onSuccess: () => { (utils as any).media.list.invalidate(); setDeleteId(null); toast.success("Mídia removida."); },
    onError: (e: any) => toast.error("Erro ao remover: " + e.message),
  });

  const reorderMutation = trpc.media.reorder.useMutation({
    onError: (e: any) => toast.error("Erro ao reordenar: " + e.message),
  });

  const refreshEmbedMutation = (trpc as any).media.refreshEmbed.useMutation({
    onSuccess: (data: any) => {
      (utils as any).media.list.invalidate();
      if (data?.hasEmbed) {
        toast.success("Embed do Instagram regenerado com sucesso!");
      } else {
        toast.error("Não foi possível obter o embed. Verifique se o post é público.");
      }
    },
    onError: (e: any) => toast.error("Erro ao regenerar embed: " + e.message),
  });

  function resetForm() {
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(null); setFilePreview(null); setTitle(""); setDuration(8);
    setPlaylistId("none");
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    const type = f.type.startsWith("video/") ? "video" : "image";
    const sizeError = getPromotionalMediaSizeError(type, f.size);
    if (sizeError) {
      toast.error(sizeError);
      e.target.value = "";
      return;
    }
    if (filePreview) URL.revokeObjectURL(filePreview);
    setFile(f);
    setFilePreview(URL.createObjectURL(f));
    setDuration(type === "video" ? 30 : 8);
  }

  async function handleUpload() {
    if (!file) return;
    const type = file.type.startsWith("video/") ? "video" : "image";
    const sizeError = getPromotionalMediaSizeError(type, file.size);
    if (sizeError) {
      toast.error(sizeError);
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      if (!base64) throw new Error("Não foi possível preparar o arquivo para envio. Selecione o vídeo novamente.");
      await uploadMutation.mutateAsync({
        type,
        base64,
        mimeType: file.type,
        fileName: file.name,
        title: getMediaTitle(title, file.name),
        displayDuration: safeDuration,
        playlistId: playlistId === "none" ? null : Number(playlistId),
      });
    } finally { setUploading(false); }
  }

  async function handleAddLink() {
    if (!externalUrl || !detectedType) return;
    const normalizedUrl = detectedType === "instagram" ? normalizeInstagramUrl(externalUrl) : externalUrl;
    if (!normalizedUrl) {
      toast.error("Use um link público de post ou Reel do Instagram, como instagram.com/reel/...");
      return;
    }
    setAddingLink(true);
    try {
      await addLinkMutation.mutateAsync({
        externalUrl: normalizedUrl,
        externalType: detectedType,
        title: getMediaTitle(externalTitle, `${detectedType === "youtube" ? "Vídeo do YouTube" : detectedType === "instagram" ? "Conteúdo do Instagram" : "Campanha Admoon"}`),
        displayDuration: safeExternalDuration,
        playlistId: playlistId === "none" ? null : Number(playlistId),
      });
    } finally { setAddingLink(false); }
  }

  function fileToBase64(f: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve((reader.result as string).split(",")[1]);
      reader.onerror = reject;
      reader.readAsDataURL(f);
    });
  }

  function handleDragStart(id: number) { dragItemRef.current = id; }
  function handleDragOver(e: React.DragEvent, id: number) { e.preventDefault(); setDragOverId(id); }
  function handleDrop(targetId: number) {
    const sourceId = dragItemRef.current;
    if (!sourceId || sourceId === targetId) { setDragOverId(null); return; }
    const sorted = [...mediaList].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
    const sourceIdx = sorted.findIndex((m) => m.id === sourceId);
    const targetIdx = sorted.findIndex((m) => m.id === targetId);
    const newOrder = [...sorted];
    const [moved] = newOrder.splice(sourceIdx, 1);
    newOrder.splice(targetIdx, 0, moved);
    reorderMutation.mutate({ orderedIds: newOrder.map((m: any) => m.id) });
    (utils as any).media.list.setData(undefined, newOrder as any);
    setDragOverId(null);
    dragItemRef.current = null;
  }

  const sorted = [...mediaList].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const activeCount = sorted.filter((m: any) => m.isActive).length;

  return (
    <DashboardLayout>
      <div className="space-y-4 md:space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-foreground flex items-center gap-2">
              <MonitorPlay className="w-6 h-6 text-primary" />
              Mídia Promocional
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Imagens, vídeos e links do Instagram/YouTube exibidos no Painel de TV
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button variant="outline" onClick={() => setPlaylistManagerOpen(true)} className="gap-2">
              <ListVideo className="w-4 h-4" /> Playlists
            </Button>
            <Button onClick={() => setUploadOpen(true)} className="gap-2">
              <Plus className="w-4 h-4" /> Adicionar Mídia
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card><CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Total de mídias</p>
            <p className="text-2xl font-bold">{sorted.length}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Ativas no painel</p>
            <p className="text-2xl font-bold text-green-600">{activeCount}</p>
          </CardContent></Card>
          <Card><CardContent className="pt-4 pb-4">
            <p className="text-sm text-muted-foreground">Desativadas</p>
            <p className="text-2xl font-bold text-muted-foreground">{sorted.length - activeCount}</p>
          </CardContent></Card>
        </div>

        {playlists.length > 0 && (
          <div className="flex gap-2 overflow-x-auto pb-1">
            {playlists.map((playlist: any) => (
              <Card key={playlist.id} className={`min-w-56 border-border/60 ${playlist.isActive ? "" : "opacity-60"}`}>
                <CardContent className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-sm font-semibold truncate">{playlist.name}</p>
                    <Badge variant={playlist.isActive ? "secondary" : "outline"}>{playlist.isActive ? "Ativa" : "Pausada"}</Badge>
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><CalendarClock className="w-3 h-3" />{playlist.startDate ? `${playlist.startDate} a ${playlist.endDate ?? "sem fim"}` : "Sem data definida"}</p>
                  <p className="text-[11px] text-muted-foreground">{playlist.startHour != null ? `${String(playlist.startHour).padStart(2, "0")}:00–${String(playlist.endHour ?? 23).padStart(2, "0")}:00` : "Dia todo"}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Instruções */}
        <Card className="border-dashed border-primary/30 bg-primary/5">
          <CardContent className="pt-4 pb-4">
            <div className="text-sm text-muted-foreground space-y-1">
              <p><strong className="text-foreground">Como funciona:</strong> As mídias ativas aparecem em slideshow automático no Painel de TV, alternando com a fila de chamada.</p>
              <p>Arraste os itens para reordenar. Clique no tempo (ex: <strong className="text-foreground">8s</strong>) para editar a duração de cada mídia individualmente.</p>
              <p>Formatos aceitos: <strong className="text-foreground">JPG, PNG, WebP, GIF, MP4, WebM</strong> — ou cole um link do <strong className="text-foreground">YouTube</strong> ou <strong className="text-foreground">Instagram</strong> (posts e reels).</p>
            </div>
          </CardContent>
        </Card>

        {/* Lista de mídias */}
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : sorted.length === 0 ? (
          <Card className="border-dashed">
            <CardContent className="py-16 text-center">
              <MonitorPlay className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
              <p className="text-muted-foreground font-medium">Nenhuma mídia cadastrada</p>
              <p className="text-sm text-muted-foreground/60 mt-1 mb-4">
                Adicione imagens, vídeos ou links do Instagram/YouTube
              </p>
              <Button variant="outline" onClick={() => setUploadOpen(true)} className="gap-2">
                <Plus className="w-4 h-4" />
                Adicionar primeira mídia
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {sorted.map((item: any) => {
              const isExternal = !!(item as MediaItem).externalUrl;
              const extType = (item as MediaItem).externalType;
              return (
                <div
                  key={item.id}
                  draggable
                  onDragStart={() => handleDragStart(item.id)}
                  onDragOver={(e) => handleDragOver(e, item.id)}
                  onDrop={() => handleDrop(item.id)}
                  onDragLeave={() => setDragOverId(null)}
                  className={`group relative rounded-xl border bg-card overflow-hidden transition-all cursor-grab active:cursor-grabbing ${
                    dragOverId === item.id ? "border-primary ring-2 ring-primary/30 scale-105" : "border-border hover:border-primary/40"
                  } ${!item.isActive ? "opacity-60" : ""}`}
                >
                  {/* Thumbnail */}
                  <div className="relative aspect-video bg-muted overflow-hidden">
                    {isExternal ? (
                      <ExternalThumbnail item={item as MediaItem} />
                    ) : item.type === "image" ? (
                      <img src={item.url} alt={item.title ?? "Mídia"} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-black/80">
                        <video src={item.url} className="w-full h-full object-cover" muted preload="metadata" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                            <Play className="w-5 h-5 text-white ml-0.5" />
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Badge de tipo */}
                    <div className="absolute top-2 left-2">
                      <Badge variant="secondary" className="gap-1 text-xs py-0.5">
                        {isExternal && extType === "youtube" ? <><Youtube className="w-3 h-3 text-red-500" /> YouTube</> :
                         isExternal && extType === "instagram" ? <><Instagram className="w-3 h-3 text-pink-500" /> Instagram</> :
                         item.type === "image" ? <><ImageIcon className="w-3 h-3" /> Imagem</> :
                         <><Video className="w-3 h-3" /> Vídeo</>}
                      </Badge>
                    </div>

                    {/* Status desativado */}
                    {!item.isActive && (
                      <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                        <Badge variant="secondary" className="gap-1"><EyeOff className="w-3 h-3" />Desativada</Badge>
                      </div>
                    )}

                    {/* Drag handle */}
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-7 h-7 rounded-md bg-black/50 backdrop-blur flex items-center justify-center">
                        <GripVertical className="w-4 h-4 text-white" />
                      </div>
                    </div>
                  </div>

                  {/* Info */}
                  <div className="p-3 space-y-2">
                    <p className="font-medium text-sm truncate">
                      {item.title ?? <span className="text-muted-foreground italic">Sem título</span>}
                    </p>

                    <Select value={item.playlistId ? String(item.playlistId) : "none"} onValueChange={(value) => updateMutation.mutate({ id: item.id, playlistId: value === "none" ? null : Number(value) })}>
                      <SelectTrigger className="h-7 text-[11px]"><SelectValue placeholder="Programação padrão" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">Programação padrão</SelectItem>
                        {playlists.map((playlist: any) => <SelectItem key={playlist.id} value={String(playlist.id)}>{playlist.name}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    {/* Duração editável inline */}
                    <DurationEditor
                      item={item as MediaItem}
                      onSave={(dur) => {
                        updateMutation.mutate({ id: item.id, displayDuration: dur });
                        toast.success(`Duração atualizada para ${dur}s`);
                      }}
                    />

                    {(item.startHour != null || item.endHour != null) && (
                      <div className="flex items-center gap-1 text-xs text-primary">
                        <Clock className="w-3 h-3" />
                        {String(item.startHour ?? 0).padStart(2, "0")}:00 – {String(item.endHour ?? 23).padStart(2, "0")}:00
                      </div>
                    )}

                    {/* Ações */}
                    <div className="flex items-center gap-1 pt-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1 flex-1"
                        onClick={() => setPreviewItem(item as MediaItem)}>
                        <Eye className="w-3 h-3" />Ver
                      </Button>
                      <Button size="sm" variant="ghost"
                        className={`h-7 px-2 text-xs gap-1 flex-1 ${item.isActive ? "text-amber-600 hover:text-amber-700" : "text-green-600 hover:text-green-700"}`}
                        onClick={() => updateMutation.mutate({ id: item.id, isActive: !item.isActive })}>
                        {item.isActive ? <><EyeOff className="w-3 h-3" />Ocultar</> : <><Eye className="w-3 h-3" />Ativar</>}
                      </Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-xs gap-1" title="Horário de exibição"
                        onClick={() => setScheduleItem(item as MediaItem)}>
                        <Clock className="w-3 h-3" />
                      </Button>
                      {(item as MediaItem).externalType === "instagram" && (
                        <Button size="sm" variant="ghost"
                          className="h-7 px-2 text-xs gap-1 text-pink-500 hover:text-pink-600"
                          title="Regenerar embed do Instagram"
                          disabled={refreshEmbedMutation.isPending}
                          onClick={() => refreshEmbedMutation.mutate({ id: item.id })}>
                          <RefreshCw className="w-3 h-3" />
                        </Button>
                      )}
                      <Button size="sm" variant="ghost" className="h-7 px-2 text-destructive hover:text-destructive"
                        onClick={() => setDeleteId(item.id)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog open={playlistManagerOpen} onOpenChange={setPlaylistManagerOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><ListVideo className="w-5 h-5" /> Nova playlist programada</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label htmlFor="playlist-name">Nome da campanha ou playlist</Label><Input id="playlist-name" value={playlistName} onChange={(e) => setPlaylistName(e.target.value)} placeholder="Ex: Oferta da tarde" className="mt-1.5" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label htmlFor="playlist-start">Início</Label><Input id="playlist-start" type="date" value={playlistStartDate} onChange={(e) => setPlaylistStartDate(e.target.value)} className="mt-1.5" /></div>
              <div><Label htmlFor="playlist-end">Fim</Label><Input id="playlist-end" type="date" value={playlistEndDate} onChange={(e) => setPlaylistEndDate(e.target.value)} className="mt-1.5" /></div>
              <div><Label htmlFor="playlist-start-hour">Das</Label><Input id="playlist-start-hour" type="time" step="3600" value={playlistStartHour} onChange={(e) => setPlaylistStartHour(e.target.value)} className="mt-1.5" /></div>
              <div><Label htmlFor="playlist-end-hour">Até</Label><Input id="playlist-end-hour" type="time" step="3600" value={playlistEndHour} onChange={(e) => setPlaylistEndHour(e.target.value)} className="mt-1.5" /></div>
            </div>
            <div>
              <Label>Dias de exibição</Label>
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"].map((day, index) => {
                  const selected = playlistDays.includes(index);
                  return <Button key={day} type="button" size="sm" variant={selected ? "default" : "outline"} className="h-7 px-2 text-xs" onClick={() => setPlaylistDays((current) => selected ? current.filter((value) => value !== index) : [...current, index].sort())}>{day}</Button>;
                })}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">Se deixar datas ou horários vazios, a playlist poderá exibir conteúdo em qualquer período. Em seguida, associe cada mídia à playlist desejada.</p>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setPlaylistManagerOpen(false)}>Fechar</Button><Button disabled={playlistName.trim().length < 2 || playlistDays.length === 0 || createPlaylistMutation.isPending} onClick={() => createPlaylistMutation.mutate({ name: playlistName.trim(), activeDays: playlistDays, startDate: playlistStartDate || null, endDate: playlistEndDate || null, startHour: playlistStartHour ? Number(playlistStartHour.split(":")[0]) : null, endHour: playlistEndHour ? Number(playlistEndHour.split(":")[0]) : null })}>Criar playlist</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ─── Modal de adicionar mídia ─── */}
      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploading && !addingLink) { setUploadOpen(o); if (!o) resetForm(); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Adicionar Mídia Promocional
            </DialogTitle>
          </DialogHeader>

          <Tabs defaultValue="upload">
            <TabsList className="w-full">
              <TabsTrigger value="upload" className="flex-1 gap-2">
                <Upload className="w-3.5 h-3.5" />
                Arquivo
              </TabsTrigger>
              <TabsTrigger value="link" className="flex-1 gap-2">
                <Link2 className="w-3.5 h-3.5" />
                Link Externo
              </TabsTrigger>
            </TabsList>

            {/* Aba: Upload de arquivo */}
            <TabsContent value="upload" className="space-y-4 mt-4">
              <div>
                <Label>Arquivo</Label>
                <label
                  htmlFor="media-upload"
                  className={`mt-1.5 flex flex-col items-center justify-center w-full rounded-xl border-2 border-dashed cursor-pointer transition-colors ${
                    file ? "border-primary/50 bg-primary/5" : "border-border hover:border-primary/40 hover:bg-muted/50"
                  }`}
                  style={{ minHeight: filePreview ? "auto" : "120px" }}
                >
                  {filePreview ? (
                    <div className="w-full rounded-xl overflow-hidden">
                      {file?.type.startsWith("video/") ? (
                        <video src={filePreview} controls className="w-full max-h-48 object-contain" />
                      ) : (
                        <img src={filePreview} alt="Preview" className="w-full max-h-48 object-contain" />
                      )}
                    </div>
                  ) : (
                    <div className="py-8 text-center px-4">
                      <Upload className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Clique para selecionar</p>
                <p className="text-xs text-muted-foreground/60 mt-1">JPG, PNG, WebP, GIF, MP4, WebM</p>
                    </div>
                  )}
                  <input id="media-upload" type="file"
                    accept="image/jpeg,image/png,image/webp,image/gif,video/mp4,video/webm"
                    className="hidden" onChange={handleFileChange} />
                </label>
                {file && <p className="text-xs text-muted-foreground mt-1">{file.name} — {(file.size / 1024 / 1024).toFixed(1)}MB</p>}
              </div>

              <div>
                <Label htmlFor="media-title">Título (opcional)</Label>
                <Input id="media-title" placeholder="Ex: Promoção de verão" value={title}
                  onChange={(e) => setTitle(e.target.value)} className="mt-1.5" />
              </div>

              <div>
                <Label>Playlist ou campanha</Label>
                <Select value={playlistId} onValueChange={setPlaylistId}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Programação padrão</SelectItem>{playlists.map((playlist: any) => <SelectItem key={playlist.id} value={String(playlist.id)}>{playlist.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="media-duration">
                  Tempo de exibição: <strong>{safeDuration}s</strong>
                  {file?.type.startsWith("video/") && <span className="text-muted-foreground font-normal"> (também usado para avançar após o vídeo)</span>}
                </Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input id="media-duration" type="range" min={MEDIA_DISPLAY_DURATION_MIN_SECONDS} max={MEDIA_DISPLAY_DURATION_MAX_SECONDS} value={safeDuration}
                    onChange={(e) => setDuration(Number(e.target.value))} className="flex-1" />
                  <span className="text-sm font-medium w-10 text-right">{safeDuration}s</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {safeDuration < 10 ? "Rápido — ideal para imagens de destaque" :
                   safeDuration < 30 ? "Padrão — bom para a maioria das imagens" :
                   "Longo — até 60 segundos por mídia"}
                </p>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => { setUploadOpen(false); resetForm(); }} disabled={uploading}>Cancelar</Button>
                <Button onClick={handleUpload} disabled={!file || uploading} className="gap-2">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Enviando..." : "Enviar"}
                </Button>
              </DialogFooter>
            </TabsContent>

            {/* Aba: Link externo */}
            <TabsContent value="link" className="space-y-4 mt-4">
              <div>
                <Label htmlFor="ext-url">Link do YouTube, Instagram ou Admoon</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="ext-url"
                    placeholder="Cole a URL de um Reel, YouTube ou player Admoon"
                    value={externalUrl}
                    onChange={(e) => setExternalUrl(e.target.value)}
                    className={`pr-10 ${detectedType ? "border-green-500/60" : ""}`}
                  />
                  {detectedType === "youtube" && <Youtube className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-red-500" />}
                  {detectedType === "instagram" && <Instagram className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-500" />}
                  {detectedType === "admooh" && <MonitorPlay className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-sky-600" />}
                </div>
                {externalUrl && !detectedType && (
                  <p className="text-xs text-amber-600 mt-1">URL não reconhecida. Use links do YouTube, Instagram ou um player Admoon.</p>
                )}
                {detectedType && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ Fonte {detectedType === "youtube" ? "YouTube" : detectedType === "instagram" ? "Instagram" : "Admoon"} detectada
                  </p>
                )}
              </div>

              <div className="bg-muted/50 rounded-lg p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Exemplos de links aceitos:</p>
                <p>• YouTube: <code className="bg-muted px-1 rounded">youtube.com/watch?v=...</code> ou <code className="bg-muted px-1 rounded">youtu.be/...</code></p>
                <p>• Instagram: <code className="bg-muted px-1 rounded">instagram.com/p/...</code> ou <code className="bg-muted px-1 rounded">instagram.com/reel/...</code></p>
                <p>• Admoon: URL pública de player, campanha ou playlist fornecida pela sua conta Admoon.</p>
              </div>

              <div>
                <Label htmlFor="ext-title">Título (opcional)</Label>
                <Input id="ext-title" placeholder="Ex: Reels da semana" value={externalTitle}
                  onChange={(e) => setExternalTitle(e.target.value)} className="mt-1.5" />
              </div>

              <div>
                <Label>Playlist ou campanha</Label>
                <Select value={playlistId} onValueChange={setPlaylistId}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Programação padrão</SelectItem>{playlists.map((playlist: any) => <SelectItem key={playlist.id} value={String(playlist.id)}>{playlist.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="ext-duration">
                  Tempo de exibição: <strong>{safeExternalDuration}s</strong>
                  <span className="text-muted-foreground font-normal"> (tempo antes de avançar para a próxima mídia)</span>
                </Label>
                <div className="flex items-center gap-3 mt-1.5">
                  <input id="ext-duration" type="range" min={MEDIA_DISPLAY_DURATION_MIN_SECONDS} max={MEDIA_DISPLAY_DURATION_MAX_SECONDS} value={safeExternalDuration}
                    onChange={(e) => setExternalDuration(Number(e.target.value))} className="flex-1" />
                  <span className="text-sm font-medium w-10 text-right">{safeExternalDuration}s</span>
                </div>
              </div>

              <DialogFooter>
                <Button variant="outline" onClick={() => setUploadOpen(false)} disabled={addingLink}>Cancelar</Button>
                <Button onClick={handleAddLink} disabled={!externalUrl || !detectedType || addingLink} className="gap-2">
                  {addingLink ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                  {addingLink ? "Adicionando..." : "Adicionar Link"}
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Modal de preview */}
      <Dialog open={!!previewItem} onOpenChange={(o) => !o && setPreviewItem(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{previewItem?.title ?? "Preview"}</DialogTitle>
          </DialogHeader>
          {previewItem?.externalType === "youtube" ? (
            <div className="aspect-video w-full rounded-lg overflow-hidden">
              <iframe
                src={getYoutubeEmbedUrl(previewItem.externalUrl ?? "") ?? ""}
                className="w-full h-full"
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </div>
          ) : previewItem?.externalType === "instagram" ? (
            <div className="relative aspect-[9/16] max-h-[60vh] mx-auto overflow-hidden rounded-lg bg-black">
              <iframe src={getInstagramEmbedUrl(previewItem.externalUrl ?? "") ?? ""} title="Preview do Instagram" className="h-full w-full border-0" allow="autoplay; encrypted-media; fullscreen" referrerPolicy="strict-origin-when-cross-origin" />
              <a href={normalizeInstagramUrl(previewItem.externalUrl ?? "") ?? previewItem.externalUrl ?? "#"} target="_blank" rel="noreferrer" className="absolute bottom-3 right-3 rounded-full bg-pink-600 px-3 py-1.5 text-xs font-semibold text-white shadow">Abrir no Instagram</a>
            </div>
          ) : previewItem?.externalType === "admooh" ? (
            <div className="flex min-h-72 flex-col items-center justify-center gap-3 rounded-lg bg-gradient-to-br from-sky-700 via-blue-600 to-indigo-800 p-6 text-center text-white">
              <MonitorPlay className="h-12 w-12" />
              <p className="font-semibold">Fonte de campanha Admoon</p>
              <p className="max-w-md text-sm text-white/90">O Painel de TV tentará exibir este player na sequência de mídias. A URL deve ter sido autorizada pela Admoon para reprodução em tela.</p>
              <a href={previewItem.externalUrl ?? "#"} target="_blank" rel="noreferrer" className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-blue-700">Abrir fonte Admoon</a>
            </div>
          ) : previewItem?.type === "image" ? (
            <img src={previewItem.url} alt={previewItem.title ?? ""} className="w-full rounded-lg max-h-[60vh] object-contain" />
          ) : previewItem?.type === "video" ? (
            <video src={previewItem.url} controls autoPlay className="w-full rounded-lg max-h-[60vh]" />
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Modal de horário */}
      {scheduleItem && (
        <ScheduleModal
          item={scheduleItem}
          onClose={() => setScheduleItem(null)}
          onSave={(startHour, endHour) => {
            updateMutation.mutate({ id: scheduleItem.id, startHour, endHour });
            toast.success(startHour != null
              ? `Horário definido: ${String(startHour).padStart(2, "0")}:00 – ${String(endHour ?? 23).padStart(2, "0")}:00`
              : "Exibição sem restrição de horário");
          }}
        />
      )}

      {/* Confirmação de exclusão */}
      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remover mídia?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta ação não pode ser desfeita. A mídia será removida do Painel de TV.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteId && deleteMutation.mutate({ id: deleteId })}
            >
              Remover
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
